import { useEffect, useState } from "react";

/** Prototype ICONS.mail (outline, 24px grid). */
const MAIL = "M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z M3 7l9 6 9-6";

/** A resend waits this long after the last send, so the inbox is not flooded (QA ON-14). */
export const RESEND_COOLDOWN_S = 30;

interface Props {
  email: string;
  /** "Use a different address": back to the sign-in card, with the address kept. */
  onBack: () => void;
  /** "Resend link": true when a new link is on its way. */
  onResend: () => Promise<boolean>;
}

/** Prototype renderMagic: replaces the whole sign-in card once the link is on its way. */
function MagicLinkSentCard({ email, onBack, onResend }: Props) {
  const [wait, setWait] = useState(RESEND_COOLDOWN_S);
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "failed">("idle");

  useEffect(() => {
    if (wait <= 0) return undefined;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const resend = async () => {
    if (wait > 0 || status === "sending") return;
    setStatus("sending");
    const ok = await onResend();
    setStatus(ok ? "sent" : "failed");
    if (ok) setWait(RESEND_COOLDOWN_S);
  };

  return (
    <div className="plg-ob-center" role="status">
      <div className="plg-ob-icon" aria-hidden="true">
        <svg className="plg-line-icon" viewBox="0 0 24 24">
          <path d={MAIL} />
        </svg>
      </div>
      <h1 className="plg-signup__title">Check your inbox</h1>
      <p className="plg-signup__sub">We’ve sent you a secure login link. For your safety, the link will expire in 15 minutes.</p>
      {email && <p className="plg-ob-hint">{email}</p>}
      {status === "sent" && <p className="plg-ob-hint">We sent a new link.</p>}
      {status === "failed" && <p className="plg-ob-hint">We could not send a new link. Please try again.</p>}
      <div className="plg-signup__foot plg-signup__foot--center">
        <button type="button" className="plg-signup__foot-link" onClick={onBack}>
          Use a different address
        </button>
        <button type="button" className="plg-signup__foot-link" onClick={resend} disabled={wait > 0 || status === "sending"}>
          {wait > 0 ? `Resend link in ${wait}s` : "Resend link"}
        </button>
      </div>
    </div>
  );
}

export default MagicLinkSentCard;
